import { useEffect, useState, useCallback, useRef } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Package, Users, ArrowUpCircle, ArrowDownCircle, BarChart3, ScanLine,
  Plus, Edit, Trash2, AlertTriangle, Search, X, Mail, Send, History,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LINE_LABELS = {
  retail: "Retail",
  professional: "Professional",
  espositore: "Espositori",
  marketing: "Marketing",
};
const LINE_COLORS = {
  retail: "bg-rose-100 text-rose-700",
  professional: "bg-violet-100 text-violet-700",
  espositore: "bg-amber-100 text-amber-700",
  marketing: "bg-sky-100 text-sky-700",
};

const apiFetch = async (path, opts = {}) => {
  const token = localStorage.getItem("token");
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
};

export default function WarehousePage() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50/40 via-white to-amber-50/30">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[#5A3A3A] flex items-center gap-2">
            <Package className="w-7 h-7 text-[#B87A8A]" />
            Magazzino ODR
          </h1>
          <p className="text-sm text-gray-500">Gestione prodotti, scorte e movimenti</p>
        </header>

        <div className="flex gap-1 mb-5 border-b border-rose-100 overflow-x-auto" data-testid="warehouse-tabs">
          {[
            { id: "dashboard", label: "Dashboard", Icon: BarChart3 },
            { id: "scan", label: "Scanner", Icon: ScanLine },
            { id: "products", label: "Prodotti", Icon: Package },
            { id: "movements", label: "Movimenti", Icon: ArrowUpCircle },
            { id: "persons", label: "Persone", Icon: Users },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition ${
                tab === t.id ? "bg-white border border-b-white border-rose-200 text-[#5A3A3A] -mb-px" : "text-gray-500 hover:text-[#5A3A3A]"
              }`}
            >
              <t.Icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <Dashboard />}
        {tab === "scan" && <Scanner />}
        {tab === "products" && <ProductsTab />}
        {tab === "movements" && <MovementsTab />}
        {tab === "persons" && <PersonsTab />}
      </div>
    </div>
  );
}

// ============ Dashboard ============
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [recipientEdit, setRecipientEdit] = useState("");
  const [editingRcp, setEditingRcp] = useState(false);
  const [sending, setSending] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState([]);

  const loadStats = useCallback(() => {
    apiFetch("/warehouse/stats").then(r => r.json()).then(setStats).catch(() => {});
  }, []);
  const loadRecipient = useCallback(() => {
    apiFetch("/warehouse/alerts/recipient").then(r => r.json()).then(d => {
      setRecipient(d.recipient || "");
      setRecipientEdit(d.recipient || "");
    });
  }, []);
  useEffect(() => { loadStats(); loadRecipient(); }, [loadStats, loadRecipient]);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await apiFetch("/warehouse/alerts/send-now", { method: "POST" });
      const d = await r.json();
      if (d.success) toast.success(`Email inviata a ${d.recipient} con ${d.count} prodott${d.count===1?'o':'i'} sotto soglia`);
      else toast.info(d.reason || "Nessun prodotto sotto soglia");
    } catch { toast.error("Errore di rete"); }
    setSending(false);
  };

  const saveRecipient = async () => {
    if (!recipientEdit.includes("@")) { toast.error("Email non valida"); return; }
    const r = await apiFetch("/warehouse/alerts/recipient", {
      method: "PUT", body: JSON.stringify({ recipient: recipientEdit }),
    });
    if (r.ok) {
      const d = await r.json();
      setRecipient(d.recipient);
      setEditingRcp(false);
      toast.success("Destinatario aggiornato");
    } else toast.error("Errore salvataggio");
  };

  const loadLog = async () => {
    const r = await apiFetch("/warehouse/alerts/log?limit=20");
    const d = await r.json();
    setLog(d.items || []);
    setShowLog(true);
  };

  if (!stats) return <div className="text-center text-gray-400 py-10">Caricamento...</div>;

  const lowCount = stats.low_stock_count || 0;
  return (
    <div className="space-y-6">
      {/* BANNER ALERT */}
      {lowCount > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-rose-50 border-2 border-amber-300 rounded-2xl p-4 flex items-center gap-4 animate-pulse-slow" data-testid="alert-banner">
          <div className="bg-amber-500 text-white rounded-full p-2.5 shrink-0">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-amber-900">
              {lowCount} prodott{lowCount === 1 ? "o" : "i"} sotto soglia minima
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              L'email viene inviata in automatico a <b>{recipient || "info@ita318.it"}</b> al primo movimento sotto soglia (1 volta ogni 24h per prodotto).
            </p>
          </div>
          <Button
            onClick={sendNow}
            disabled={sending}
            className="bg-amber-600 hover:bg-amber-700 shrink-0"
            data-testid="send-alert-now"
          >
            <Send className="w-4 h-4 mr-1" />
            {sending ? "Invio..." : "Invia email ora"}
          </Button>
        </div>
      )}

      {/* CARD CONFIG EMAIL */}
      <div className="bg-white border border-rose-100 rounded-2xl p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <Mail className="w-4 h-4 text-[#B87A8A]" />
            <span className="text-gray-500">Email alert scorte basse:</span>
            {editingRcp ? (
              <Input
                value={recipientEdit}
                onChange={(e) => setRecipientEdit(e.target.value)}
                className="h-8 w-64 text-sm"
                data-testid="alert-recipient-input"
              />
            ) : (
              <span className="font-mono font-semibold text-[#5A3A3A]" data-testid="alert-recipient-display">{recipient}</span>
            )}
          </div>
          <div className="flex gap-2">
            {editingRcp ? (
              <>
                <Button size="sm" variant="outline" onClick={() => { setEditingRcp(false); setRecipientEdit(recipient); }}>Annulla</Button>
                <Button size="sm" onClick={saveRecipient} className="bg-[#B87A8A] hover:bg-[#a06978]" data-testid="alert-recipient-save">Salva</Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => setEditingRcp(true)} data-testid="alert-recipient-edit">
                  <Edit className="w-3 h-3 mr-1" /> Modifica
                </Button>
                <Button size="sm" variant="outline" onClick={loadLog} data-testid="alert-log-btn">
                  <History className="w-3 h-3 mr-1" /> Storico email
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Prodotti attivi", value: stats.products, color: "text-rose-600", bg: "bg-rose-50" },
          { label: "Persone in anagrafica", value: stats.persons, color: "text-violet-600", bg: "bg-violet-50" },
          { label: "Movimenti totali", value: stats.movements_total, color: "text-sky-600", bg: "bg-sky-50" },
          { label: "Scorte basse", value: stats.low_stock_count, color: "text-amber-600", bg: "bg-amber-50" },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-2xl p-4`} data-testid={`stat-${s.label}`}>
            <p className="text-xs text-gray-600">{s.label}</p>
            <p className={`text-3xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>
      {stats.low_stock_items?.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-2xl p-4" data-testid="low-stock-list">
          <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Prodotti sotto soglia minima
          </h3>
          <div className="divide-y divide-amber-100">
            {stats.low_stock_items.map(p => (
              <div key={p.product_id} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium">{p.name}</span>
                  <span className="text-xs text-gray-500 ml-2">({p.sku})</span>
                </div>
                <div className="text-right">
                  <span className="text-amber-700 font-bold">{p.stock}</span>
                  <span className="text-gray-400 text-xs"> / min {p.min_stock}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODAL STORICO */}
      {showLog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowLog(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="alert-log-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[#5A3A3A] flex items-center gap-2">
                <History className="w-5 h-5" /> Storico email alert
              </h3>
              <button onClick={() => setShowLog(false)}><X className="w-5 h-5" /></button>
            </div>
            {log.length === 0 ? (
              <p className="text-center text-gray-400 py-6">Nessuna email inviata finora</p>
            ) : (
              <div className="divide-y divide-rose-50">
                {log.map(a => (
                  <div key={a.alert_id} className="py-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-[#5A3A3A]">{a.recipient}</span>
                      <span className="text-xs text-gray-400">{new Date(a.sent_at).toLocaleString("it-IT")}</span>
                    </div>
                    <p className="text-xs text-gray-500">{a.products_count} prodotti notificati</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Scanner ============
function Scanner() {
  const [ean, setEan] = useState("");
  const [product, setProduct] = useState(null);
  const [type, setType] = useState("out");
  const [qty, setQty] = useState(1);
  const [personId, setPersonId] = useState("");
  const [persons, setPersons] = useState([]);
  const [destination, setDestination] = useState("");
  const [notes, setNotes] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    apiFetch("/warehouse/persons").then(r => r.json()).then(d => setPersons(d.items || []));
    inputRef.current?.focus();
  }, []);

  const scan = async (code) => {
    if (!code.trim()) return;
    try {
      const r = await apiFetch(`/warehouse/products/scan/${encodeURIComponent(code.trim())}`);
      if (r.ok) {
        setProduct(await r.json());
      } else {
        toast.error(`Codice "${code}" non trovato`);
        setProduct(null);
      }
    } catch {
      toast.error("Errore di rete");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter") scan(ean);
  };

  const submit = async () => {
    if (!product) return;
    try {
      const r = await apiFetch("/warehouse/movements", {
        method: "POST",
        body: JSON.stringify({
          type, product_id: product.product_id, quantity: parseInt(qty),
          person_id: personId, destination, notes,
        }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`${type === "in" ? "Entrata" : "Uscita"} registrata. Nuovo stock: ${d.new_stock}`);
        if (d.low_stock) toast.warning(`⚠ Scorta sotto soglia minima (${d.new_stock})`);
        setEan(""); setProduct(null); setQty(1); setDestination(""); setNotes(""); setPersonId("");
        inputRef.current?.focus();
      } else {
        toast.error(d.detail || "Errore");
      }
    } catch {
      toast.error("Errore di rete");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="bg-white border border-rose-100 rounded-2xl p-5 space-y-4">
        <h3 className="font-semibold text-[#5A3A3A] flex items-center gap-2">
          <ScanLine className="w-5 h-5" /> Scansiona o digita EAN / SKU
        </h3>
        <Input
          ref={inputRef}
          value={ean}
          onChange={(e) => setEan(e.target.value)}
          onKeyDown={onKey}
          placeholder="Codice EAN-13 o SKU"
          className="text-lg h-12 font-mono"
          data-testid="scanner-input"
        />
        <Button onClick={() => scan(ean)} className="w-full bg-[#B87A8A] hover:bg-[#a06978]">
          Cerca prodotto
        </Button>
        {product && (
          <div className="border-t pt-4 space-y-3" data-testid="scanner-product">
            <div className="bg-rose-50 rounded-xl p-3">
              <p className="font-bold text-[#5A3A3A]">{product.name}</p>
              <p className="text-xs text-gray-600">{product.sku} · {LINE_LABELS[product.line]} · Stock attuale: <b>{product.stock}</b></p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={type} onChange={(e) => setType(e.target.value)} className="border rounded-lg px-3 h-10 text-sm">
                <option value="out">Uscita</option>
                <option value="in">Entrata</option>
              </select>
              <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Quantità" data-testid="scanner-qty" />
            </div>
            <select value={personId} onChange={(e) => setPersonId(e.target.value)} className="border rounded-lg px-3 h-10 text-sm w-full" data-testid="scanner-person">
              <option value="">-- Seleziona persona (opzionale) --</option>
              {persons.map(p => <option key={p.person_id} value={p.person_id}>{p.name} ({p.role})</option>)}
            </select>
            <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Destinazione / cliente" />
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note" />
            <Button onClick={submit} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="scanner-submit">
              Registra movimento
            </Button>
          </div>
        )}
      </div>
      <div className="bg-white border border-rose-100 rounded-2xl p-5 text-sm text-gray-600 space-y-3">
        <h4 className="font-semibold text-[#5A3A3A]">Come usare lo scanner</h4>
        <ol className="space-y-2 list-decimal list-inside">
          <li>Click nel campo "Codice EAN-13" per metterlo a fuoco</li>
          <li>Spara con la pistola sul codice a barre del prodotto</li>
          <li>Si compila in automatico, premi Invio o "Cerca"</li>
          <li>Verifica il prodotto, scegli "Entrata" / "Uscita", quantità, persona</li>
          <li>Click "Registra movimento" — lo stock si aggiorna in tempo reale</li>
        </ol>
        <p className="text-xs text-gray-400 mt-3">
          Se un EAN non è ancora associato, vai sui Prodotti, modifica la card e incolla l'EAN.
        </p>
      </div>
    </div>
  );
}

// ============ Products ============
function ProductsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [lineFilter, setLineFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    const qs = lineFilter ? `?line=${lineFilter}` : "";
    apiFetch(`/warehouse/products${qs}`).then(r => r.json()).then(d => setItems(d.items || []));
  }, [lineFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(p => !filter || p.name.toLowerCase().includes(filter.toLowerCase()) || p.sku.toLowerCase().includes(filter.toLowerCase()) || (p.ean || "").includes(filter));

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cerca per nome, SKU o EAN" className="pl-9" data-testid="products-search" />
        </div>
        <select value={lineFilter} onChange={(e) => setLineFilter(e.target.value)} className="h-10 border rounded-lg px-3 text-sm">
          <option value="">Tutte le linee</option>
          {Object.entries(LINE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Button onClick={() => setShowNew(true)} className="bg-[#B87A8A] hover:bg-[#a06978]" data-testid="new-product-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo prodotto
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(p => (
          <ProductCard key={p.product_id} p={p} onEdit={() => setEditing(p)} onDelete={async () => {
            if (!window.confirm(`Eliminare "${p.name}"?`)) return;
            await apiFetch(`/warehouse/products/${p.product_id}`, { method: "DELETE" });
            toast.success("Eliminato"); load();
          }} />
        ))}
      </div>
      {filtered.length === 0 && <p className="text-center text-gray-400 py-8">Nessun prodotto</p>}

      {(editing || showNew) && (
        <ProductForm
          product={editing}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={() => { load(); setEditing(null); setShowNew(false); }}
        />
      )}
    </div>
  );
}

function ProductCard({ p, onEdit, onDelete }) {
  const low = p.stock <= p.min_stock;
  return (
    <div className="bg-white border border-rose-100 rounded-xl p-4 space-y-2" data-testid={`product-card-${p.product_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#5A3A3A] truncate" title={p.name}>{p.name}</p>
          <p className="text-[11px] text-gray-500 font-mono">{p.sku} {p.ean ? `· EAN ${p.ean}` : ""}</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${LINE_COLORS[p.line] || "bg-gray-100"}`}>
          {LINE_LABELS[p.line] || p.line}
        </span>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <p className={`text-2xl font-bold ${low ? "text-amber-600" : "text-emerald-600"}`}>{p.stock}</p>
          <p className="text-[10px] text-gray-400">min: {p.min_stock} {p.ml && `· ${p.ml}ml`}</p>
        </div>
        {low && <span className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> sotto soglia</span>}
      </div>
      {p.expiry && <p className="text-[10px] text-gray-500">Scad: {p.expiry} {p.lot && `· Lotto ${p.lot}`}</p>}
      <div className="flex gap-1 pt-2 border-t border-rose-50">
        <button onClick={onEdit} className="flex-1 text-xs text-blue-600 hover:bg-blue-50 rounded py-1 flex items-center justify-center gap-1" data-testid={`edit-${p.product_id}`}>
          <Edit className="w-3 h-3" /> Modifica
        </button>
        <button onClick={onDelete} className="flex-1 text-xs text-red-500 hover:bg-red-50 rounded py-1 flex items-center justify-center gap-1" data-testid={`delete-${p.product_id}`}>
          <Trash2 className="w-3 h-3" /> Elimina
        </button>
      </div>
    </div>
  );
}

function ProductForm({ product, onClose, onSaved }) {
  const [f, setF] = useState(product || { line: "retail", category: "face", min_stock: 50, stock: 0, price: 0 });
  const submit = async () => {
    if (!f.sku || !f.name) { toast.error("SKU e Nome obbligatori"); return; }
    const path = product ? `/warehouse/products/${product.product_id}` : "/warehouse/products";
    const r = await apiFetch(path, { method: product ? "PUT" : "POST", body: JSON.stringify(f) });
    const d = await r.json();
    if (r.ok) { toast.success("Salvato"); onSaved(); }
    else toast.error(d.detail || "Errore");
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="product-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#5A3A3A]">{product ? "Modifica prodotto" : "Nuovo prodotto"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="SKU *" value={f.sku || ""} onChange={(e) => setF({ ...f, sku: e.target.value })} data-testid="form-sku" />
          <Input placeholder="EAN-13" value={f.ean || ""} onChange={(e) => setF({ ...f, ean: e.target.value })} data-testid="form-ean" />
        </div>
        <Input placeholder="Nome prodotto *" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="form-name" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.line || "retail"} onChange={(e) => setF({ ...f, line: e.target.value })} className="border rounded-lg px-3 h-10 text-sm">
            {Object.entries(LINE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Input placeholder="Categoria (face/body/hair/...)" value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Input placeholder="Formato (200ml)" value={f.ml || ""} onChange={(e) => setF({ ...f, ml: e.target.value })} />
          <Input type="number" placeholder="Prezzo" value={f.price ?? 0} onChange={(e) => setF({ ...f, price: parseFloat(e.target.value) || 0 })} />
          <Input type="number" placeholder="Stock attuale" value={f.stock ?? 0} onChange={(e) => setF({ ...f, stock: parseInt(e.target.value) || 0 })} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Input type="number" placeholder="Soglia min" value={f.min_stock ?? 0} onChange={(e) => setF({ ...f, min_stock: parseInt(e.target.value) || 0 })} />
          <Input placeholder="Lotto" value={f.lot || ""} onChange={(e) => setF({ ...f, lot: e.target.value })} />
          <Input type="date" placeholder="Scadenza" value={f.expiry || ""} onChange={(e) => setF({ ...f, expiry: e.target.value })} data-testid="form-expiry" />
        </div>
        <Input placeholder="Ubicazione" value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} />
        <Input placeholder="Note" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Annulla</Button>
          <Button onClick={submit} className="flex-1 bg-[#B87A8A] hover:bg-[#a06978]" data-testid="form-submit">Salva</Button>
        </div>
      </div>
    </div>
  );
}

// ============ Movements ============
function MovementsTab() {
  const [items, setItems] = useState([]);
  const load = () => apiFetch("/warehouse/movements?limit=300").then(r => r.json()).then(d => setItems(d.items || []));
  useEffect(load, []);
  return (
    <div className="bg-white border border-rose-100 rounded-2xl divide-y divide-rose-50" data-testid="movements-list">
      {items.length === 0 && <p className="text-center text-gray-400 py-8">Nessun movimento</p>}
      {items.map(m => (
        <div key={m.movement_id} className="p-3 flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            {m.type === "in" ? <ArrowDownCircle className="w-5 h-5 text-emerald-500" /> : <ArrowUpCircle className="w-5 h-5 text-rose-500" />}
            <div>
              <p className="font-medium">{m.product_name || "?"} <span className="text-gray-400 text-xs">({m.product_sku})</span></p>
              <p className="text-xs text-gray-500">
                {m.person_name && `${m.person_name} · `}
                {m.destination && `${m.destination} · `}
                {new Date(m.created_at).toLocaleString("it-IT")}
              </p>
              {m.notes && <p className="text-[11px] text-gray-400 italic">{m.notes}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className={`font-bold text-lg ${m.type === "in" ? "text-emerald-600" : "text-rose-600"}`}>
              {m.type === "in" ? "+" : "-"}{m.quantity}
            </p>
            <button onClick={async () => {
              if (!window.confirm("Stornare questo movimento?")) return;
              await apiFetch(`/warehouse/movements/${m.movement_id}`, { method: "DELETE" });
              load();
            }} className="text-[10px] text-red-400 hover:underline" data-testid={`undo-${m.movement_id}`}>Storna</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ Persons ============
function PersonsTab() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => apiFetch("/warehouse/persons").then(r => r.json()).then(d => setItems(d.items || []));
  useEffect(load, []);
  return (
    <div className="space-y-4">
      <Button onClick={() => setShow(true)} className="bg-[#B87A8A] hover:bg-[#a06978]" data-testid="new-person-btn">
        <Plus className="w-4 h-4 mr-1" /> Nuova persona
      </Button>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(p => (
          <div key={p.person_id} className="bg-white border border-rose-100 rounded-xl p-4" data-testid={`person-${p.person_id}`}>
            <p className="font-semibold text-[#5A3A3A]">{p.name}</p>
            <p className="text-xs text-violet-600 uppercase tracking-wide">{p.role}</p>
            {p.azienda && <p className="text-xs text-gray-500">{p.azienda}</p>}
            {p.phone && <p className="text-xs text-gray-500">📞 {p.phone}</p>}
            {p.email && <p className="text-xs text-gray-500">✉ {p.email}</p>}
            <div className="flex gap-2 pt-2 border-t border-rose-50 mt-2">
              <button onClick={() => { setEditing(p); setShow(true); }} className="text-xs text-blue-600 hover:underline">Modifica</button>
              <button onClick={async () => {
                if (!window.confirm(`Eliminare ${p.name}?`)) return;
                await apiFetch(`/warehouse/persons/${p.person_id}`, { method: "DELETE" });
                load();
              }} className="text-xs text-red-500 hover:underline">Elimina</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-gray-400 col-span-3 py-8">Nessuna persona</p>}
      </div>
      {show && <PersonForm person={editing} onClose={() => { setShow(false); setEditing(null); }} onSaved={() => { load(); setShow(false); setEditing(null); }} />}
    </div>
  );
}

function PersonForm({ person, onClose, onSaved }) {
  const [f, setF] = useState(person || { role: "magazziniere" });
  const submit = async () => {
    if (!f.name) { toast.error("Nome obbligatorio"); return; }
    const path = person ? `/warehouse/persons/${person.person_id}` : "/warehouse/persons";
    const r = await apiFetch(path, { method: person ? "PUT" : "POST", body: JSON.stringify(f) });
    if (r.ok) { toast.success("Salvato"); onSaved(); }
    else toast.error("Errore");
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="person-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#5A3A3A]">{person ? "Modifica persona" : "Nuova persona"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <Input placeholder="Nome e cognome *" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="person-name" />
        <select value={f.role || "magazziniere"} onChange={(e) => setF({ ...f, role: e.target.value })} className="border rounded-lg px-3 h-10 text-sm w-full">
          {["magazziniere", "agente", "distributore", "rappresentante", "segretaria", "cliente", "altro"].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <Input placeholder="Azienda" value={f.azienda || ""} onChange={(e) => setF({ ...f, azienda: e.target.value })} />
        <Input placeholder="Telefono" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <Input placeholder="Email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Input placeholder="Note" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Annulla</Button>
          <Button onClick={submit} className="flex-1 bg-[#B87A8A] hover:bg-[#a06978]" data-testid="person-submit">Salva</Button>
        </div>
      </div>
    </div>
  );
}
